import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { io, Socket } from 'socket.io-client';
import type { RootState } from '../../store';
import {
  PaperAirplaneIcon,
  UserCircleIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import Button from '../../components/ui/Button';
// import Card, { CardContent } from '../../components/ui/Card';
import { messagesService, type Conversation, type DirectMessage } from '../../services/messagesService';

const SOCKET_URL = import.meta.env.VITE_SOCKET_URL || 'http://localhost:3402';

const MessagesPage: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const navigate = useNavigate();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<DirectMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    loadConversations();

    // Setup Socket.io connection
    const token = localStorage.getItem('accessToken');
    if (token && user?.id) {
      const socket = io(SOCKET_URL, {
        transports: ['websocket'],
        auth: { token }
      });
      
      socketRef.current = socket;
      socket.emit('authenticate', user.id);

      socket.on('direct_message', (message: DirectMessage) => {
        if (selectedConversation && (
          message.senderId === selectedConversation.userId ||
          message.receiverId === selectedConversation.userId
        )) {
          setMessages(prev => [...prev, message]);
        } else {
          // Update conversation list if message is for current user
          if (message.receiverId === user.id) {
            loadConversations();
          }
        }
        scrollToBottom();
      });

      return () => {
        socket.disconnect();
      };
    }
    return () => {};
  }, [user?.id]);

  useEffect(() => {
    if (selectedConversation) {
      loadMessages(selectedConversation.userId);
      if (socketRef.current) {
        socketRef.current.emit('join_direct_message', `dm_${user?.id}_${selectedConversation.userId}`);
      }
    }
    return () => {
      if (socketRef.current && selectedConversation) {
        socketRef.current.emit('leave_direct_message', `dm_${user?.id}_${selectedConversation.userId}`);
      }
    };
  }, [selectedConversation?.userId, user?.id]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadConversations = async () => {
    try {
      setLoading(true);
      const res = await messagesService.getConversations();
      setConversations(res.conversations || []);
    } catch (error) {
      console.error('Failed to load conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (userId: string) => {
    try {
      const res = await messagesService.getConversation(userId);
      setMessages(res.messages || []);
      scrollToBottom();
    } catch (error) {
      console.error('Failed to load messages:', error);
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation || sending) return;

    setSending(true);
    try {
      const res = await messagesService.sendMessage({
        receiverId: selectedConversation.userId,
        content: newMessage.trim()
      });
      setMessages(prev => [...prev, res.message]);
      setNewMessage('');
      await loadConversations(); // Refresh to update last message
      scrollToBottom();
    } catch (error: any) {
      console.error('Failed to send message:', error);
      alert(error?.response?.data?.message || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const filteredConversations = conversations.filter(conv =>
    conv.partner.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
    conv.partner.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    conv.partner.lastName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading && conversations.length === 0) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-10 bg-gray-200 rounded w-1/3 mb-6"></div>
          <div className="grid grid-cols-3 gap-6">
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-20 bg-gray-200 rounded"></div>
              ))}
            </div>
            <div className="col-span-2 bg-gray-200 rounded h-96"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-8rem)] flex flex-col p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Messages</h1>
        <p className="text-gray-600">Connect with creators and patrons</p>
      </div>

      <div className="flex-1 grid grid-cols-3 gap-6 overflow-hidden">
        {/* Conversations List */}
        <div className="flex flex-col border border-gray-200 rounded-xl overflow-hidden">
          <div className="p-4 border-b border-gray-200 bg-white">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search conversations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-offset-2"
                style={{ '--tw-ring-color': 'var(--patreon-orange)' } as React.CSSProperties}
                onFocus={(e) => { e.target.style.borderColor = 'var(--patreon-orange)'; e.target.style.boxShadow = '0 0 0 3px rgba(255, 107, 53, 0.1)'; }}
                onBlur={(e) => { e.target.style.borderColor = ''; e.target.style.boxShadow = ''; }}
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto bg-white">
            {filteredConversations.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <p>No conversations yet</p>
                <p className="text-sm mt-2">Start messaging creators or patrons!</p>
              </div>
            ) : (
              filteredConversations.map((conv) => (
                <button
                  key={conv.userId}
                  onClick={() => setSelectedConversation(conv)}
                  className={`w-full p-4 text-left border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                    selectedConversation?.userId === conv.userId ? 'bg-[rgba(255,107,53,0.05)]' : ''
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    {conv.partner.avatar ? (
                      <img
                        src={conv.partner.avatar}
                        alt={conv.partner.username}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold patreon-primary-gradient" style={{ background: 'var(--patreon-btn-primary-bg)' }}>
                        {(conv.partner.firstName || conv.partner.username).charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <p className="font-semibold text-gray-900 truncate">
                          {conv.partner.firstName && conv.partner.lastName
                            ? `${conv.partner.firstName} ${conv.partner.lastName}`
                            : conv.partner.username}
                        </p>
                        {conv.unreadCount > 0 && (
                          <span className="ml-2 px-2 py-0.5 rounded-full text-xs font-medium text-white" style={{ backgroundColor: 'var(--patreon-orange)' }}>
                            {conv.unreadCount}
                          </span>
                        )}
                      </div>
                      {conv.lastMessage && (
                        <p className="text-sm text-gray-600 truncate">
                          {conv.lastMessage.content}
                        </p>
                      )}
                      {conv.lastMessage && (
                        <p className="text-xs text-gray-400 mt-1">
                          {formatDate(conv.lastMessage.createdAt)}
                        </p>
                      )}
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Messages View */}
        <div className="col-span-2 flex flex-col border border-gray-200 rounded-xl overflow-hidden bg-white">
          {selectedConversation ? (
            <>
              {/* Message Header */}
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {selectedConversation.partner.avatar ? (
                    <img
                      src={selectedConversation.partner.avatar}
                      alt={selectedConversation.partner.username}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold patreon-primary-gradient" style={{ background: 'var(--patreon-btn-primary-bg)' }}>
                      {(selectedConversation.partner.firstName || selectedConversation.partner.username).charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div>
                    <h3 className="font-semibold text-gray-900">
                      {selectedConversation.partner.firstName && selectedConversation.partner.lastName
                        ? `${selectedConversation.partner.firstName} ${selectedConversation.partner.lastName}`
                        : selectedConversation.partner.username}
                    </h3>
                    <button
                      onClick={() => navigate(`/creator/${selectedConversation.partner.username}`)}
                      className="text-sm" style={{ color: 'var(--patreon-orange)' }}
                    >
                      View profile
                    </button>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedConversation(null)}
                  className="p-2 text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>

              {/* Messages List */}
              <div className="flex-1 overflow-y-auto p-6 space-y-4">
                {messages.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <p>No messages yet</p>
                    <p className="text-sm mt-2">Start the conversation!</p>
                  </div>
                ) : (
                  messages.map((message) => {
                    const isOwn = message.senderId === user?.id;
                    return (
                      <div
                        key={message.id}
                        className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                      >
                        <div className={`max-w-[70%] ${isOwn ? 'order-2' : 'order-1'}`}>
                          <div className={`rounded-lg px-4 py-2 ${
                            isOwn
                              ? 'rounded-br-sm'
                              : 'rounded-bl-sm'
                          }`}
                          style={isOwn 
                            ? { backgroundColor: 'var(--patreon-btn-primary-bg)', color: 'white' }
                            : { backgroundColor: '#F3F4F6', color: 'var(--patreon-black)' }
                          }>
                            <p className="text-sm whitespace-pre-wrap break-words">{message.content}</p>
                            <div className="flex items-center justify-end mt-1">
                              <p className={`text-xs ${isOwn ? 'text-white/70' : 'text-gray-400'}`}>
                                {formatDate(message.createdAt)}
                              </p>
                              {isOwn && message.isRead && (
                                <CheckCircleIcon className="w-3 h-3 ml-1 text-white/70" />
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Message Input */}
              <form onSubmit={handleSendMessage} className="p-4 border-t border-gray-200">
                <div className="flex items-center space-x-3">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type a message..."
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-offset-2"
                    style={{ '--tw-ring-color': 'var(--patreon-orange)' } as React.CSSProperties}
                    onFocus={(e) => { e.target.style.borderColor = 'var(--patreon-orange)'; e.target.style.boxShadow = '0 0 0 3px rgba(255, 107, 53, 0.1)'; }}
                    onBlur={(e) => { e.target.style.borderColor = ''; e.target.style.boxShadow = ''; }}
                  />
                  <Button
                    type="submit"
                    disabled={!newMessage.trim() || sending}
                    leftIcon={<PaperAirplaneIcon className="w-4 h-4" />}
                  >
                    Send
                  </Button>
                </div>
              </form>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-center">
              <div>
                <UserCircleIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Select a conversation</h3>
                <p className="text-gray-600">Choose a conversation from the list to start messaging</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MessagesPage;


